import { useState, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import ProductGrid from "@/components/shop/ProductGrid";
import ProductFilters, { FilterState } from "@/components/shop/ProductFilters";
import { products, resolveCategorySlug } from "@/data/products";
import { getCollectionsByCategory, categoryMeta, Collection } from "@/data/collections";

// Category hero images
const categoryImages: Record<string, string> = {
  hardwood:
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&auto=format&fit=crop",
  vinyl:
    "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=1920&auto=format&fit=crop",
  carpet:
    "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1920&auto=format&fit=crop",
  tile:
    "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1920&auto=format&fit=crop",
  laminate:
    "https://images.unsplash.com/photo-1595428774223-ef52624120d2?w=1920&auto=format&fit=crop",
};

const Products = () => {
  const { category: categoryParam } = useParams<{ category: string }>();
  const location = useLocation();
  
  // Resolve category from URL path
  const pathSegments = location.pathname.split('/');
  const urlSlug = pathSegments[pathSegments.length - 1];
  const category = resolveCategorySlug(categoryParam || urlSlug || "hardwood");
  
  const meta = categoryMeta[category] || categoryMeta.hardwood;
  const categoryCollections = getCollectionsByCategory(category);

  const [filters, setFilters] = useState<FilterState>({
    collections: [],
    colors: [],
    brands: [],
    priceRange: [0, 20],
  });

  // Filter products based on category and active filters
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (product.category !== category) return false;

      if (
        filters.collections.length > 0 &&
        !filters.collections.includes(product.collection)
      ) {
        return false;
      }

      if (
        filters.colors.length > 0 &&
        !product.colors.some((c) => filters.colors.includes(c))
      ) {
        return false;
      }

      if (filters.brands.length > 0 && !filters.brands.includes(product.brand)) {
        return false;
      }

      return true;
    });
  }, [category, filters]);

  return (
    <Layout>
      <PageHero
        title={meta.title}
        subtitle={meta.description}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Products" },
          { label: meta.title },
        ]}
        backgroundImage={categoryImages[category]}
        overlay
      />

      {/* Collections Section */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
              Our {meta.title.replace(" Flooring", "")} Collections
            </h2>
            <p className="text-muted-foreground mt-2">
              Explore our curated collections designed to perform
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {categoryCollections.map((collection: Collection, index: number) => (
              <motion.div
                key={collection.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/products/${category}/${collection.slug}`}
                  className="block group"
                >
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4">
                    <img
                      src={collection.heroImage}
                      alt={collection.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-block text-xs font-medium text-white/80 uppercase tracking-wider mb-1">
                        {collection.subtitle}
                      </span>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {collection.name}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-sm text-white/90 font-medium group-hover:text-primary transition-colors">
                        Explore Collection
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Products Section */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
            {/* Filters Sidebar */}
            <ProductFilters
              filters={filters}
              onFiltersChange={setFilters}
              productCount={filteredProducts.length}
            />

            {/* Product Grid */}
            <div>
              <div className="hidden lg:flex items-center justify-between mb-6">
                <p className="text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{filteredProducts.length}</span> products
                </p>
              </div>
              <ProductGrid products={filteredProducts} />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Products;
